'use client';

import React, { memo, useCallback } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Button from '../primitives/Button/Button';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useSetupSignlessTrading } from 'src/hooks/wallets/privy/useSetupSignlessTrading';
import useModal from '../Modals/hooks/useModal';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useAppInitialLoadContext } from 'src/providers/app/AppInitialLoadProvider/AppInitialLoadProvider';

export const PerpTradeFormPrimaryButton = memo(
	function PerpTradeFormPrimaryButtonMemo({
		side,
		currentMarketSymbol,
		onSubmit,
		hasNoCollateral,
		isMarketInSettlement,
		disabled,
		isLoading,
		dataTestId,
	}: {
		side: 'buy' | 'sell';
		currentMarketSymbol: string;
		onSubmit: () => void;
		hasNoCollateral: boolean;
		isMarketInSettlement: boolean;
		disabled: boolean;
		isLoading?: boolean;
		dataTestId?: string;
	}) {
		const connected = useWalletIsConnected();
		const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
		const isMobileScreenSize = useIsMobileScreenSize();
		const { toPromptSignlessTradingSetup } = useSetupSignlessTrading();
		const { captureEvent } = usePostHogCapture();
		const { isInitialLoading } = useAppInitialLoadContext();

		const { show } = useModal();

		const size = isMobileScreenSize ? 'md' : 'lg';

		const openEnableTradingModal = useCallback(() => {
			captureEvent('onboarding_signless_modal_opened');
			show({
				type: 'enable_signless_trading',
				props: {},
			});
		}, [show, captureEvent]);

		if (!connected) {
			return (
				<Button
					className="w-full"
					size={size}
					onClick={onSubmit}
					disabled={isGeoblocked || isInitialLoading}
					dataTestId={dataTestId}
				>
					{isGeoblocked ? 'Unavailable' : 'Connect Wallet'}
				</Button>
			);
		}

		if (hasNoCollateral) {
			return (
				<Button
					className="w-full"
					size={size}
					onClick={onSubmit}
					dataTestId={dataTestId}
				>
					Deposit
				</Button>
			);
		}

		if (toPromptSignlessTradingSetup) {
			return (
				<Button
					className="w-full"
					size={size}
					onClick={openEnableTradingModal}
					dataTestId={dataTestId}
				>
					Enable Trading
				</Button>
			);
		}

		return (
			<Button
				className="w-full"
				size={size}
				variant={side === 'buy' ? 'positive' : 'negative'}
				disabled={disabled}
				isLoading={isLoading}
				onClick={onSubmit}
				dataTestId={dataTestId}
			>
				{isMarketInSettlement ? (
					<>{`${currentMarketSymbol} in settlement`}</>
				) : side === 'buy' ? (
					'Long'
				) : (
					'Short'
				)}
			</Button>
		);
	}
);
